import React, { useState, useEffect, useCallback } from 'react';
import { 
  Save, 
  X, 
  Plus,
  Edit, 
  Trash2, 
  Camera, 
  FileText, 
  MapPin, 
  User, 
  Clock,
  Download,
  Upload,
  Eye,
  MessageSquare,
  Image,
  File,
  FileBarChart,
  Shield,
  Users,
  Database,
  Archive
} from 'lucide-react';

// Import des nouveaux composants
import EnhancedOverview from './EnhancedOverview';
import ElementsEnqueteTab from './ElementsEnqueteTab';
import MediaDocumentsTab from './MediaDocumentsTab';
import QuickActionButton from './QuickActionButton';

// Import des modaux
import PersonneModal from './modals/PersonneModal';
import LieuModal from './modals/LieuModal';
import NoteModal from './modals/NoteModal';
import PhotoModal from './modals/PhotoModal';
import FichierModal from './modals/FichierModal';
import ClientModal from './modals/ClientModal';
import ConfirmationModal from './modals/ConfirmationModal';
import ExpertModal from './modals/ExpertModal';
import { ReportService, ReportOptions } from '../services/ReportService';
import { noteApi, personneApi, lieuApi, photoApi, fichierApi } from '../services/crudService';
import { enqueteService } from '../services/enqueteService';

interface EnqueteDetailData {
  id: string;
  titre: string;
  description: string;
  statut: 'en_cours' | 'terminee' | 'en_attente' | 'archivee';
  priorite: 'haute' | 'moyenne' | 'basse';
  dateCreation: string;
  dateModification: string;
  client: string | { nom: string; email?: string; telephone?: string; adresse?: string; };
  budget: number;
  assigne: string;
  tags: string[];
  
  // Informations client
  infosClient: {
    nom: string;
    email: string;
    telephone: string;
    adresse: string;
    notes: string;
  };
  
  // Personnes d'intérêt
  personnes: Array<{
    id: string;
    nom: string;
    email: string;
    telephone: string;
    adresse: string;
    description: string;
    statut: 'actif' | 'inactif' | 'suspect';
    tags: string[];
  }>;
  
  // Lieux
  lieux: Array<{
    id: string;
    nom: string;
    adresse: string;
    coordonnees: { lat: number; lng: number };
    description: string;
    type: 'domicile' | 'travail' | 'frequentation' | 'autre';
    photos: string[];
  }>;
  
  // Notes
  notes: Array<{
    id: string;
    titre: string;
    contenu: string;
    date: string;
    auteur: string;
    tags: string[];
    priorite: 'haute' | 'moyenne' | 'basse';
  }>;
  
  // Fichiers
  fichiers: Array<{
    id: string;
    nom: string;
    type: 'document' | 'photo' | 'video' | 'audio' | 'autre';
    taille: string;
    date: string;
    description: string;
    url: string;
  }>;
  
  // Photos
  photos: Array<{
    id: string;
    nom: string;
    description: string;
    date: string;
    lieu: string;
    tags: string[];
    url: string;
  }>;
  
  // Timeline
  timeline: Array<{
    id: string;
    date: string;
    type: 'note' | 'photo' | 'lieu' | 'personne' | 'fichier' | 'statut';
    titre: string;
    description: string;
    auteur: string;
  }>;
}

interface EnqueteDetailProps {
  enqueteId: string;
  onClose: () => void;
  onSave?: (enqueteData: any) => Promise<void>;
}

const EnqueteDetail: React.FC<EnqueteDetailProps> = ({ enqueteId, onClose, onSave }) => {
  const [enquete, setEnquete] = useState<EnqueteDetailData | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [isAddingPersonne, setIsAddingPersonne] = useState(false);
  const [isAddingLieu, setIsAddingLieu] = useState(false);
  const [isAddingPhoto, setIsAddingPhoto] = useState(false);
  const [isAddingFichier, setIsAddingFichier] = useState(false);
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // États pour les rapports
  const [reportOptions, setReportOptions] = useState<ReportOptions>({
    includeClient: true,
    includePersonnes: true,
    includeLieux: true,
    includeNotes: true,
    includePhotos: true,
    includeFichiers: true,
    includeTimeline: true,
    includeStats: true,
    includeObjectifs: true,
    includeElementsFournis: true,
    includeSommaire: true,
    includeConclusions: true
  });
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  
  // États pour l'édition
  const [editingPersonne, setEditingPersonne] = useState<any>(null);
  const [editingLieu, setEditingLieu] = useState<any>(null);
  const [editingNote, setEditingNote] = useState<any>(null);
  const [editingPhoto, setEditingPhoto] = useState<any>(null);
  const [editingFichier, setEditingFichier] = useState<any>(null);
  
  // États pour les confirmations de suppression
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    type: string;
    id: string;
    name: string;
  }>({
    isOpen: false,
    type: '',
    id: '',
    name: ''
  });

  const [isExpertModalOpen, setIsExpertModalOpen] = useState(false);
  const [shareStatus, setShareStatus] = useState<{
    isShared: boolean;
    sharedByName?: string;
    sharedAt?: string;
  }>({ isShared: false });

  // Initialiser le statut de partage avec les données de l'enquête
  useEffect(() => {
    if (enquete) {
      setShareStatus({
        isShared: Boolean((enquete as any).is_shared_with_admin),
        sharedByName: (enquete as any).shared_by_name || undefined,
        sharedAt: (enquete as any).shared_at || undefined
      });
    }
  }, [enquete]);
  
  // État pour la lightbox des photos
  const [lightboxPhoto, setLightboxPhoto] = useState<{
    isOpen: boolean;
    photo: any;
    photos: any[];
    currentIndex: number;
  }>({
    isOpen: false,
    photo: null,
    photos: [],
    currentIndex: 0
  });

  // Données de test
  const mockEnquete: EnqueteDetailData = {
    id: '1',
    titre: 'Enquête sur Jean Dupont',
    description: 'Recherche d\'informations sur les activités en ligne et la présence digitale',
    statut: 'en_cours',
    priorite: 'haute',
    dateCreation: '2024-01-15',
    dateModification: '2024-01-20',
    client: 'Cabinet Martin',
    budget: 5000,
    assigne: 'Détective Privé',
    tags: ['surveillance', 'digital', 'réseaux sociaux'],
    
    infosClient: {
      nom: 'Cabinet Martin',
      email: 'contact@cabinet-martin.fr',
      telephone: '01 23 45 67 89',
      adresse: '123 Rue de la Paix, 75001 Paris',
      notes: 'Client très exigeant, demande des rapports détaillés'
    },
    
    personnes: [
      {
        id: '1',
        nom: 'Jean Dupont',
        email: 'jean.dupont@email.com',
        telephone: '06 12 34 56 78',
        adresse: '456 Avenue des Champs, 75008 Paris',
        description: 'Sujet principal de l\'enquête',
        statut: 'actif',
        tags: ['principal', 'suspect']
      },
      {
        id: '2',
        nom: 'Marie Dupont',
        email: 'marie.dupont@email.com',
        telephone: '06 98 76 54 32',
        adresse: '456 Avenue des Champs, 75008 Paris',
        description: 'Épouse du sujet principal',
        statut: 'actif',
        tags: ['famille']
      }
    ],
    
    lieux: [
      {
        id: '1',
        nom: 'Domicile principal',
        adresse: '456 Avenue des Champs, 75008 Paris',
        coordonnees: { lat: 48.8566, lng: 2.3522 },
        description: 'Résidence principale du sujet',
        type: 'domicile',
        photos: ['photo1.jpg', 'photo2.jpg']
      },
      {
        id: '2',
        nom: 'Bureau de travail',
        adresse: '789 Boulevard Saint-Germain, 75006 Paris',
        coordonnees: { lat: 48.8534, lng: 2.3488 },
        description: 'Lieu de travail suspecté',
        type: 'travail',
        photos: ['photo3.jpg']
      }
    ],
    
    notes: [
      {
        id: '1',
        titre: 'Premier contact établi',
        contenu: 'Première prise de contact avec le client. Demande d\'investigation sur les activités en ligne de Jean Dupont.',
        date: '2024-01-15',
        auteur: 'Détective Privé',
        tags: ['contact', 'initial'],
        priorite: 'haute'
      },
      {
        id: '2',
        titre: 'Analyse des réseaux sociaux',
        contenu: 'Début de l\'analyse des comptes sociaux. Profils trouvés sur Facebook, LinkedIn et Instagram.',
        date: '2024-01-16',
        auteur: 'Détective Privé',
        tags: ['réseaux sociaux', 'analyse'],
        priorite: 'moyenne'
      }
    ],
    
    fichiers: [
      {
        id: '1',
        nom: 'rapport_initial.pdf',
        type: 'document',
        taille: '2.5 MB',
        date: '2024-01-15',
        description: 'Rapport initial de l\'enquête',
        url: '/files/rapport_initial.pdf'
      },
      {
        id: '2',
        nom: 'photos_surveillance.zip',
        type: 'photo',
        taille: '15.2 MB',
        date: '2024-01-18',
        description: 'Photos de surveillance',
        url: '/files/photos_surveillance.zip'
      }
    ],
    
    photos: [
      {
        id: '1',
        nom: 'Domicile principal - Vue extérieure',
        description: 'Photo du domicile principal de Jean Dupont',
        date: '2024-01-16',
        lieu: '456 Avenue des Champs, 75008 Paris',
        tags: ['domicile', 'exterieur'],
        url: '/photos/domicile_exterieur.jpg'
      },
      {
        id: '2',
        nom: 'Bureau de travail',
        description: 'Photo du bureau de travail suspecté',
        date: '2024-01-17',
        lieu: '789 Boulevard Saint-Germain, 75006 Paris',
        tags: ['travail', 'bureau'],
        url: '/photos/bureau_travail.jpg'
      }
    ],
    
    timeline: [
      {
        id: '1',
        date: '2024-01-15',
        type: 'statut',
        titre: 'Enquête créée',
        description: 'Création de l\'enquête sur Jean Dupont',
        auteur: 'Détective Privé'
      },
      {
        id: '2',
        date: '2024-01-15',
        type: 'note',
        titre: 'Premier contact établi',
        description: 'Contact initial avec le client',
        auteur: 'Détective Privé'
      },
      {
        id: '3',
        date: '2024-01-16',
        type: 'lieu',
        titre: 'Domicile principal ajouté',
        description: 'Ajout du domicile principal',
        auteur: 'Détective Privé'
      },
      {
        id: '4',
        date: '2024-01-16',
        type: 'photo',
        titre: 'Photo domicile ajoutée',
        description: 'Photo du domicile principal',
        auteur: 'Détective Privé'
      }
    ]
  };

  useEffect(() => {
    const loadEnqueteData = async () => {
      try {
        if (process.env.NODE_ENV !== 'production') {
          console.log('Loading enquete data for ID:', enqueteId);
        }
        const response = await enqueteService.getEnqueteById(enqueteId);
        
        if (response && response.data) {
          const enqueteData = response.data.enquete;
          
          // Convertir les données de l'API vers le format attendu par le composant
          const formattedEnquete: EnqueteDetailData = {
            id: enqueteData.id,
            titre: enqueteData.titre,
            description: enqueteData.description || '',
            statut: enqueteData.statut,
            priorite: enqueteData.priorite,
            dateCreation: enqueteData.dateCreation || new Date(Date.now()).toISOString().split('T')[0],
            dateModification: enqueteData.dateModification || new Date(Date.now()).toISOString().split('T')[0],
            client: enqueteData.client || '',
            budget: enqueteData.budget || 0,
            assigne: enqueteData.assigne || '',
            tags: enqueteData.tags || [],
            
            // Informations client - pour l'instant vides, à remplir avec de vraies données
            infosClient: {
              nom: typeof enqueteData.client === 'object' && enqueteData.client !== null ? (enqueteData.client as any).nom : enqueteData.client || '',
              email: typeof enqueteData.client === 'object' && enqueteData.client !== null ? (enqueteData.client as any).email : '',
              telephone: typeof enqueteData.client === 'object' && enqueteData.client !== null ? (enqueteData.client as any).telephone : '',
              adresse: typeof enqueteData.client === 'object' && enqueteData.client !== null ? (enqueteData.client as any).adresse : '',
              notes: typeof enqueteData.client === 'object' && enqueteData.client !== null ? (enqueteData.client as any).notes : ''
            },
            
            // Listes vides pour l'instant - à charger séparément
            personnes: [],
            lieux: [],
            notes: [],
            fichiers: [],
            photos: [],
            timeline: []
          };
          
          setEnquete(formattedEnquete);
          if (process.env.NODE_ENV !== 'production') {
            console.log('Enquete data loaded successfully:', formattedEnquete);
          }
          
          // Mettre à jour le statut de partage
          setShareStatus({
            isShared: enqueteData.is_shared_with_admin || false,
            sharedByName: enqueteData.shared_by_name,
            sharedAt: enqueteData.shared_at
          });
          
          // Charger les données associées
          await loadAssociatedData(enqueteId);
        } else {
          console.error('Failed to load enquete data:', response);
          // En cas d'erreur, charger les données mockées comme fallback
          setEnquete(mockEnquete);
        }
      } catch (error) {
        console.error('Error loading enquete data:', error);
        // En cas d'erreur, charger les données mockées comme fallback
        setEnquete(mockEnquete);
      }
    };

    if (enqueteId) {
      loadEnqueteData();
    } else {
      // Si pas d'ID, utiliser les données mockées pour les tests
      setEnquete(mockEnquete);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enqueteId]);

  // Fonction pour charger les données associées (personnes, lieux, notes)
  const loadAssociatedData = async (enqueteId: string) => {
    try {
      // Charger toutes les données en parallèle pour éviter les appels multiples à setEnquete
      const [personnesResponse, lieuxResponse, notesResponse, photosResponse, fichiersResponse] = await Promise.allSettled([
        personneApi.getByEnqueteId(enqueteId),
        lieuApi.getByEnqueteId(enqueteId),
        noteApi.getByEnqueteId(enqueteId),
        photoApi.getByEnqueteId(enqueteId),
        fichierApi.getByEnqueteId(enqueteId)
      ]);

      // Traiter les résultats
      const personnes = personnesResponse.status === 'fulfilled' && personnesResponse.value ? 
        personnesResponse.value.map((p: any) => ({
          id: p.id,
          nom: p.nom,
          email: p.email || '',
          telephone: p.telephone || '',
          adresse: p.adresse || '',
          description: p.description || '',
          statut: p.statut || 'actif',
          tags: p.tags || []
        })) : [];

      const lieux = lieuxResponse.status === 'fulfilled' && lieuxResponse.value ? 
        lieuxResponse.value.map((l: any) => ({
          id: l.id,
          nom: l.nom,
          adresse: l.adresse,
          coordonnees: {
            lat: l.latitude || 0,
            lng: l.longitude || 0
          },
          description: l.description || '',
          type: l.typeLieu || l.type_lieu || 'autre',
          photos: []
        })) : [];

      const notes = notesResponse.status === 'fulfilled' && notesResponse.value ? 
        notesResponse.value.map((n: any) => ({
          id: n.id,
          titre: n.titre,
          contenu: n.contenu,
          date: new Date(n.dateCreation || Date.now()).toISOString().split('T')[0],
          auteur: 'Détective Privé',
          tags: n.tags || [],
          priorite: n.importance || 'moyenne'
        })) : [];

      const photos = photosResponse.status === 'fulfilled' && photosResponse.value ? 
        photosResponse.value.map((p: any) => ({
          id: p.id,
          nom: p.titre || p.nom || 'Photo sans nom',
          description: p.description || '',
          date: p.datePrise || new Date().toISOString().split('T')[0],
          lieu: '', // TODO: Add lieu relation
          tags: p.tags || [],
          url: p.url
        })) : [];

      const fichiers = fichiersResponse.status === 'fulfilled' && fichiersResponse.value ? 
        fichiersResponse.value.map((f: any) => ({
          id: f.id,
          nom: f.nom,
          type: f.typeFichier || 'autre',
          taille: f.taille ? `${f.taille} bytes` : 'Inconnue',
          date: new Date().toISOString().split('T')[0],
          description: f.description || '',
          url: f.url
        })) : [];

      // Mettre à jour l'état en une seule fois
      setEnquete(prev => prev ? {
        ...prev,
        personnes,
        lieux,
        notes,
        photos,
        fichiers
      } : null);

      if (process.env.NODE_ENV !== 'production') {
        console.log('Associated data loaded successfully');
      }
    } catch (error) {
      console.error('Error loading associated data:', error);
    }
  };

  // Fonction de sauvegarde
  const handleSave = async () => {
    if (!enquete || !onSave) return;
    
    try {
      setIsSaving(true);
      await onSave(enquete);
      // Optionnel : Afficher un message de succès
      console.log('Enquête sauvegardée avec succès');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      // Optionnel : Afficher un message d'erreur
    } finally {
      setIsSaving(false);
    }
  };

  // Fonctions de gestion des personnes
  const handleAddPersonne = async (personneData: any) => {
    if (!enquete) return;
    
    try {
      const personneToCreate = {
        ...personneData,
        enqueteId: enqueteId
      };
      
      const response = await personneApi.create(personneToCreate);
      
      if (response) {
        // Recharger les personnes depuis l'API
        const personnesResponse = await personneApi.getByEnqueteId(enqueteId);
        if (personnesResponse) {
          setEnquete(prev => prev ? {
            ...prev,
            personnes: personnesResponse.map((p: any) => ({
              id: p.id,
              nom: p.nom,
              email: p.email || '',
              telephone: p.telephone || '',
              adresse: p.adresse || '',
              description: p.description || '',
              statut: p.statut || 'actif',
              tags: p.tags || []
            })),
            timeline: [
              {
                id: Date.now().toString(),
                date: new Date().toISOString().split('T')[0],
                type: 'personne' as const,
                titre: `Personne ajoutée: ${personneData.nom}`,
                description: `Nouvelle personne d'intérêt ajoutée à l'enquête`,
                auteur: 'Détective Privé'
              },
              ...prev.timeline
            ]
          } : null);
        }
        
        // Auto-save after person creation
        if (onSave && enquete) {
          setTimeout(() => handleSave(), 100);
        }
        
        console.log('Person created successfully:', response);
      }
    } catch (error) {
      console.error('Error creating person:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleEditPersonne = async (personneData: any) => {
    if (!enquete || !editingPersonne) return;
    
    try {
      const response = await personneApi.update(editingPersonne.id, personneData);
      
      if (response) {
        // Update the person in local state
        const updatedPersonne = response;
        setEnquete(prev => ({
          ...prev!,
          personnes: prev!.personnes.map(p => 
            p.id === editingPersonne.id ? {
              ...p,
              nom: updatedPersonne.nom,
              email: updatedPersonne.email || '',
              telephone: updatedPersonne.telephone || '',
              adresse: updatedPersonne.adresse || '',
              description: (updatedPersonne as any).description || '',
              statut: (updatedPersonne as any).statut || 'actif',
              tags: (updatedPersonne as any).tags || []
            } : p
          ),
          timeline: [
            {
              id: Date.now().toString(),
              date: new Date().toISOString().split('T')[0],
              type: 'personne' as const,
              titre: `Personne modifiée: ${personneData.nom}`,
              description: `Informations de la personne mises à jour`,
              auteur: 'Détective Privé'
            },
            ...prev!.timeline
          ]
        }));
        console.log('Person updated successfully:', updatedPersonne);
      }
    } catch (error) {
      console.error('Error updating person:', error);
      // You might want to show an error message to the user here
    }
    
    setEditingPersonne(null);
  };

  const handleDeletePersonne = async (id: string) => {
    if (!enquete) return;
    
    try {
      const personne = enquete.personnes.find(p => p.id === id);
      const response = await personneApi.delete(id);
      
      if (response) {
        // Remove the person from local state
        setEnquete(prev => ({
          ...prev!,
          personnes: prev!.personnes.filter(p => p.id !== id),
          timeline: [
            {
              id: Date.now().toString(),
              date: new Date().toISOString().split('T')[0],
              type: 'personne' as const,
              titre: `Personne supprimée: ${personne?.nom}`,
              description: `Personne d'intérêt supprimée de l'enquête`,
              auteur: 'Détective Privé'
            },
            ...prev!.timeline
          ]
        }));
        console.log('Person deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting person:', error);
      // You might want to show an error message to the user here
    }
  };

  // Fonctions de gestion des lieux
  const handleAddLieu = async (lieuData: any) => {
    if (!enquete) return;
    
    try {
      const lieuToCreate = {
        ...lieuData,
        enqueteId: enqueteId
      };
      
      const response = await lieuApi.create(lieuToCreate);
      
      if (response) {
        // Add the new location to local state
        const newLieu = response;
        setEnquete(prev => ({
          ...prev!,
          lieux: [...prev!.lieux, {
            id: newLieu.id!,
            nom: newLieu.nom,
            adresse: newLieu.adresse,
            coordonnees: { 
              lat: newLieu.latitude || 0, 
              lng: newLieu.longitude || 0 
            },
            description: newLieu.description || '',
            type: (newLieu as any).typeLieu || 'autre',
            photos: []
          }],
          timeline: [
            {
              id: Date.now().toString(),
              date: new Date().toISOString().split('T')[0],
              type: 'lieu' as const,
              titre: `Lieu ajouté: ${lieuData.nom}`,
              description: `Nouveau lieu d'intérêt ajouté à l'enquête`,
              auteur: 'Détective Privé'
            },
            ...prev!.timeline
          ]
        }));
        console.log('Location created successfully:', newLieu);
      }
    } catch (error) {
      console.error('Error creating location:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleEditLieu = async (lieuData: any) => {
    if (!enquete || !editingLieu) return;
    
    try {
      const response = await lieuApi.update(editingLieu.id, lieuData);
      
      if (response) {
        // Update the location in local state
        const updatedLieu = response;
        setEnquete(prev => ({
          ...prev!,
          lieux: prev!.lieux.map(l => 
            l.id === editingLieu.id ? {
              ...l,
              nom: updatedLieu.nom,
              adresse: updatedLieu.adresse,
              coordonnees: { 
                lat: updatedLieu.latitude || 0, 
                lng: updatedLieu.longitude || 0 
              },
              description: updatedLieu.description || '',
              type: (updatedLieu as any).typeLieu || 'autre'
            } : l
          ),
          timeline: [
            {
              id: Date.now().toString(),
              date: new Date().toISOString().split('T')[0],
              type: 'lieu' as const,
              titre: `Lieu modifié: ${lieuData.nom}`,
              description: `Informations du lieu mises à jour`,
              auteur: 'Détective Privé'
            },
            ...prev!.timeline
          ]
        }));
        console.log('Location updated successfully:', updatedLieu);
      }
    } catch (error) {
      console.error('Error updating location:', error);
      // You might want to show an error message to the user here
    }
    
    setEditingLieu(null);
  };

  const handleDeleteLieu = async (id: string) => {
    if (!enquete) return;
    
    try {
      const lieu = enquete.lieux.find(l => l.id === id);
      const response = await lieuApi.delete(id);
      
      if (response) {
        // Remove the location from local state
        setEnquete(prev => ({
          ...prev!,
          lieux: prev!.lieux.filter(l => l.id !== id),
          timeline: [
            {
              id: Date.now().toString(),
              date: new Date().toISOString().split('T')[0],
              type: 'lieu' as const,
              titre: `Lieu supprimé: ${lieu?.nom}`,
              description: `Lieu d'intérêt supprimé de l'enquête`,
              auteur: 'Détective Privé'
            },
            ...prev!.timeline
          ]
        }));
        console.log('Location deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting location:', error);
      // You might want to show an error message to the user here
    }
  };

  // Fonctions de gestion des notes
  const handleAddNote = async (noteData: any) => {
    if (!enquete) return;
    
    try {
      const noteToCreate = {
        ...noteData,
        enqueteId: enqueteId
      };
      
      const response = await noteApi.create(noteToCreate);
      
      if (response) {
        // Recharger les notes depuis l'API
        const notesResponse = await noteApi.getByEnqueteId(enqueteId);
        if (notesResponse && notesResponse.length > 0) {
          setEnquete(prev => prev ? {
            ...prev,
            notes: notesResponse.map((n: any) => ({
              id: n.id,
              titre: n.titre,
              contenu: n.contenu,
              date: new Date(n.dateCreation || Date.now()).toISOString().split('T')[0],
              auteur: 'Détective Privé',
              tags: n.tags || [],
              priorite: n.importance || 'moyenne'
            })),
            timeline: [
              {
                id: Date.now().toString(),
                date: new Date().toISOString().split('T')[0],
                type: 'note' as const,
                titre: `Note ajoutée: ${noteData.titre}`,
                description: `Nouvelle note ajoutée à l'enquête`,
                auteur: 'Détective Privé'
              },
              ...prev.timeline
            ]
          } : null);
        }
        console.log('Note created successfully:', response);
      }
    } catch (error) {
      console.error('Error creating note:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleEditNote = async (noteData: any) => {
    if (!enquete || !editingNote) return;
    
    try {
      const response = await noteApi.update(editingNote.id, noteData);
      
      if (response) {
        // Update the note in local state
        const updatedNote = response;
        setEnquete(prev => ({
          ...prev!,
          notes: prev!.notes.map(n => 
            n.id === editingNote.id ? {
              ...n,
              titre: (updatedNote as any).titre,
              contenu: (updatedNote as any).contenu,
              tags: (updatedNote as any).tags || [],
              priorite: (updatedNote as any).importance || 'moyenne'
            } : n
          ),
          timeline: [
            {
              id: Date.now().toString(),
              date: new Date().toISOString().split('T')[0],
              type: 'note' as const,
              titre: `Note modifiée: ${noteData.titre}`,
              description: `Note mise à jour`,
              auteur: 'Détective Privé'
            },
            ...prev!.timeline
          ]
        }));
        console.log('Note updated successfully:', updatedNote);
      }
    } catch (error) {
      console.error('Error updating note:', error);
      // You might want to show an error message to the user here
    }
    
    setEditingNote(null);
  };

  const handleDeleteNote = async (id: string) => {
    if (!enquete) return;
    
    try {
      const note = enquete.notes.find(n => n.id === id);
      const response = await noteApi.delete(id);
      
      if (response) {
        // Remove the note from local state
        setEnquete(prev => ({
          ...prev!,
          notes: prev!.notes.filter(n => n.id !== id),
          timeline: [
            {
              id: Date.now().toString(),
              date: new Date().toISOString().split('T')[0],
              type: 'note' as const,
              titre: `Note supprimée: ${note?.titre}`,
              description: `Note supprimée de l'enquête`,
              auteur: 'Détective Privé'
            },
            ...prev!.timeline
          ]
        }));
        console.log('Note deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      // You might want to show an error message to the user here
    }
  };

  // Fonctions de gestion des photos
  const handleAddPhoto = async (photoData: any) => {
    if (!enquete) return;
    
    try {
      // Store current enquete ID for upload
      localStorage.setItem('currentEnqueteId', enqueteId);
      
      const photoToCreate = {
        ...photoData,
        enqueteId: enqueteId
      };
      
      const response = await photoApi.create(photoToCreate);
      
      if (response) {
        // Add the new photo to local state
        const newPhoto = response;
        setEnquete(prev => ({
          ...prev!,
          photos: [...prev!.photos, {
            id: newPhoto.id!,
            nom: (newPhoto as any).titre || (newPhoto as any).nom,
            description: newPhoto.description || '',
            date: (newPhoto as any).datePrise || new Date().toISOString().split('T')[0],
            lieu: '', // TODO: Add lieu relation
            tags: (newPhoto as any).tags || [],
            url: newPhoto.url
          }],
          timeline: [
            {
              id: Date.now().toString(),
              date: new Date().toISOString().split('T')[0],
              type: 'photo' as const,
              titre: `Photo ajoutée: ${photoData.nom}`,
              description: `Nouvelle photo ajoutée à l'enquête`,
              auteur: 'Détective Privé'
            },
            ...prev!.timeline
          ]
        }));
        console.log('Photo created successfully:', newPhoto);
      }
    } catch (error) {
      console.error('Error creating photo:', error);
      // Fallback to local state for development
      const newPhoto = {
        id: Date.now().toString(),
        ...photoData
      };
      
      setEnquete(prev => ({
        ...prev!,
        photos: [...prev!.photos, newPhoto],
        timeline: [
          {
            id: Date.now().toString(),
            date: new Date().toISOString().split('T')[0],
            type: 'photo' as const,
            titre: `Photo ajoutée: ${photoData.nom}`,
            description: `Nouvelle photo ajoutée à l'enquête`,
            auteur: 'Détective Privé'
          },
          ...prev!.timeline
        ]
      }));
    }
  };

  const handleEditPhoto = async (photoData: any) => {
    if (!enquete || !editingPhoto) return;
    
    try {
      const response = await photoApi.update(editingPhoto.id, photoData);
      
      if (response) {
        // Update the photo in local state
        const updatedPhoto = response;
        setEnquete(prev => ({
          ...prev!,
          photos: prev!.photos.map(p => 
            p.id === editingPhoto.id ? {
              ...p,
              nom: (updatedPhoto as any).titre || (updatedPhoto as any).nom,
              description: updatedPhoto.description || '',
              tags: (updatedPhoto as any).tags || [],
              url: updatedPhoto.url
            } : p
          ),
          timeline: [
            {
              id: Date.now().toString(),
              date: new Date().toISOString().split('T')[0],
              type: 'photo' as const,
              titre: `Photo modifiée: ${photoData.nom}`,
              description: `Photo mise à jour`,
              auteur: 'Détective Privé'
            },
            ...prev!.timeline
          ]
        }));
        console.log('Photo updated successfully:', updatedPhoto);
      }
    } catch (error) {
      console.error('Error updating photo:', error);
      // Fallback to local state
      setEnquete(prev => ({
        ...prev!,
        photos: prev!.photos.map(p => 
          p.id === editingPhoto.id ? { ...p, ...photoData } : p
        ),
        timeline: [
          {
            id: Date.now().toString(),
            date: new Date().toISOString().split('T')[0],
            type: 'photo' as const,
            titre: `Photo modifiée: ${photoData.nom}`,
            description: `Photo mise à jour`,
            auteur: 'Détective Privé'
          },
          ...prev!.timeline
        ]
      }));
    }
    
    setEditingPhoto(null);
  };

  const handleDeletePhoto = async (id: string) => {
    if (!enquete) return;
    
    try {
      const photo = enquete.photos.find(p => p.id === id);
      const response = await photoApi.delete(id);
      
      if (response) {
        // Remove the photo from local state
        setEnquete(prev => ({
          ...prev!,
          photos: prev!.photos.filter(p => p.id !== id),
          timeline: [
            {
              id: Date.now().toString(),
              date: new Date().toISOString().split('T')[0],
              type: 'photo' as const,
              titre: `Photo supprimée: ${photo?.nom}`,
              description: `Photo supprimée de l'enquête`,
              auteur: 'Détective Privé'
            },
            ...prev!.timeline
          ]
        }));
        console.log('Photo deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting photo:', error);
      // Fallback to local state
      const photo = enquete.photos.find(p => p.id === id);
      setEnquete(prev => ({
        ...prev!,
        photos: prev!.photos.filter(p => p.id !== id),
        timeline: [
          {
            id: Date.now().toString(),
            date: new Date().toISOString().split('T')[0],
            type: 'photo' as const,
            titre: `Photo supprimée: ${photo?.nom}`,
            description: `Photo supprimée de l'enquête`,
            auteur: 'Détective Privé'
          },
          ...prev!.timeline
        ]
      }));
    }
  };

  // Fonctions de gestion des fichiers
  const handleAddFichier = async (fichierData: any) => {
    if (!enquete) return;
    
    try {
      // Store current enquete ID for upload
      localStorage.setItem('currentEnqueteId', enqueteId);
      
      const fichierToCreate = {
        ...fichierData,
        enqueteId: enqueteId
      };
      
      const response = await fichierApi.create(fichierToCreate);
      
      if (response) {
        // Add the new file to local state
        const newFichier = response;
        setEnquete(prev => ({
          ...prev!,
          fichiers: [...prev!.fichiers, {
            id: newFichier.id!,
            nom: newFichier.nom,
            type: (newFichier as any).typeFichier || 'autre',
            taille: (newFichier as any).taille ? `${(newFichier as any).taille} bytes` : 'Inconnue',
            date: new Date().toISOString().split('T')[0],
            description: newFichier.description || '',
            url: newFichier.url
          }],
          timeline: [
            {
              id: Date.now().toString(),
              date: new Date().toISOString().split('T')[0],
              type: 'fichier' as const,
              titre: `Fichier ajouté: ${fichierData.nom}`,
              description: `Nouveau fichier ajouté à l'enquête`,
              auteur: 'Détective Privé'
            },
            ...prev!.timeline
          ]
        }));
        console.log('File created successfully:', newFichier);
      }
    } catch (error) {
      console.error('Error creating file:', error);
      // Fallback to local state for development
      const newFichier = {
        id: Date.now().toString(),
        ...fichierData
      };
      
      setEnquete(prev => ({
        ...prev!,
        fichiers: [...prev!.fichiers, newFichier],
        timeline: [
          {
            id: Date.now().toString(),
            date: new Date().toISOString().split('T')[0],
            type: 'fichier' as const,
            titre: `Fichier ajouté: ${fichierData.nom}`,
            description: `Nouveau fichier ajouté à l'enquête`,
            auteur: 'Détective Privé'
          },
          ...prev!.timeline
        ]
      }));
    }
  };

  const handleEditFichier = async (fichierData: any) => {
    if (!enquete || !editingFichier) return;
    
    try {
      const response = await fichierApi.update(editingFichier.id, fichierData);
      
      if (response) {
        // Update the file in local state
        const updatedFichier = response;
        setEnquete(prev => ({
          ...prev!,
          fichiers: prev!.fichiers.map(f => 
            f.id === editingFichier.id ? {
              ...f,
              nom: updatedFichier.nom,
              type: (updatedFichier as any).typeFichier || 'autre',
              description: updatedFichier.description || '',
              url: updatedFichier.url
            } : f
          ),
          timeline: [
            {
              id: Date.now().toString(),
              date: new Date().toISOString().split('T')[0],
              type: 'fichier' as const,
              titre: `Fichier modifié: ${fichierData.nom}`,
              description: `Fichier mis à jour`,
              auteur: 'Détective Privé'
            },
            ...prev!.timeline
          ]
        }));
        console.log('File updated successfully:', updatedFichier);
      }
    } catch (error) {
      console.error('Error updating file:', error);
      // Fallback to local state
      setEnquete(prev => ({
        ...prev!,
        fichiers: prev!.fichiers.map(f => 
          f.id === editingFichier.id ? { ...f, ...fichierData } : f
        ),
        timeline: [
          {
            id: Date.now().toString(),
            date: new Date().toISOString().split('T')[0],
            type: 'fichier' as const,
            titre: `Fichier modifié: ${fichierData.nom}`,
            description: `Fichier mis à jour`,
            auteur: 'Détective Privé'
          },
          ...prev!.timeline
        ]
      }));
    }
    
    setEditingFichier(null);
  };

  const handleDeleteFichier = async (id: string) => {
    if (!enquete) return;
    
    try {
      const fichier = enquete.fichiers.find(f => f.id === id);
      const response = await fichierApi.delete(id);
      
      if (response) {
        // Remove the file from local state
        setEnquete(prev => ({
          ...prev!,
          fichiers: prev!.fichiers.filter(f => f.id !== id),
          timeline: [
            {
              id: Date.now().toString(),
              date: new Date().toISOString().split('T')[0],
              type: 'fichier' as const,
              titre: `Fichier supprimé: ${fichier?.nom}`,
              description: `Fichier supprimé de l'enquête`,
              auteur: 'Détective Privé'
            },
            ...prev!.timeline
          ]
        }));
        console.log('File deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      // Fallback to local state
      const fichier = enquete.fichiers.find(f => f.id === id);
      setEnquete(prev => ({
        ...prev!,
        fichiers: prev!.fichiers.filter(f => f.id !== id),
        timeline: [
          {
            id: Date.now().toString(),
            date: new Date().toISOString().split('T')[0],
            type: 'fichier' as const,
            titre: `Fichier supprimé: ${fichier?.nom}`,
            description: `Fichier supprimé de l'enquête`,
            auteur: 'Détective Privé'
          },
          ...prev!.timeline
        ]
      }));
    }
  };

  // Fonction de gestion du statut de partage
  const handleShareStatusChange = (isShared: boolean, sharedByName?: string, sharedAt?: string) => {
    setShareStatus({
      isShared,
      sharedByName,
      sharedAt
    });
  };

  // Fonction de gestion des informations client
  const handleUpdateClient = async (clientData: any) => {
    if (!enquete) return;
    
    try {
      // Update enquete with new client info
      const updateData = {
        titre: enquete.titre,
        description: enquete.description,
        statut: enquete.statut,
        priorite: enquete.priorite,
        budget: enquete.budget,
        clientId: enquete.client,
        assigneA: enquete.assigne,
        tags: enquete.tags,
        infosClient: clientData
      };

      const response = await enqueteService.updateEnquete(enqueteId, updateData);
      
      if (response) {
        // Update local state
        setEnquete(prev => ({
          ...prev!,
          infosClient: { ...clientData },
          timeline: [
            {
              id: Date.now().toString(),
              date: new Date().toISOString().split('T')[0],
              type: 'note' as const,
              titre: `Informations client mises à jour`,
              description: `Coordonnées client modifiées`,
              auteur: 'Détective Privé'
            },
            ...prev!.timeline
          ]
        }));
        console.log('✅ Client info updated successfully');
      } else {
        console.error('❌ Failed to update client info:', response);
      }
    } catch (error) {
      console.error('❌ Error updating client info:', error);
      // Update local state anyway for better UX
      setEnquete(prev => ({
        ...prev!,
        infosClient: { ...clientData }
      }));
    }
  };

  // Fonction de génération de rapport

  const handleGenerateReport = async () => {
    if (!enquete) return;
    
    setIsGeneratingReport(true);
    try {
      await ReportService.generatePDF(enquete, reportOptions);
    } catch (error) {
      console.error('Erreur lors de la génération du rapport:', error);
      alert('Erreur lors de la génération du rapport');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Fonction de confirmation de suppression
  const confirmDelete = (type: string, id: string, name: string) => {
    setDeleteConfirmation({
      isOpen: true,
      type,
      id,
      name
    });
  };

  // Fonctions pour la lightbox
  const openLightbox = (photo: any, photos: any[]) => {
    const currentIndex = photos.findIndex(p => p.id === photo.id);
    setLightboxPhoto({
      isOpen: true,
      photo,
      photos,
      currentIndex
    });
  };

  const closeLightbox = () => {
    setLightboxPhoto({
      isOpen: false,
      photo: null,
      photos: [],
      currentIndex: 0
    });
  };

  const navigateLightbox = useCallback((direction: 'prev' | 'next') => {
    if (lightboxPhoto.photos.length === 0) return;
    
    let newIndex: number;
    if (direction === 'prev') {
      newIndex = lightboxPhoto.currentIndex > 0 ? lightboxPhoto.currentIndex - 1 : lightboxPhoto.photos.length - 1;
    } else {
      newIndex = lightboxPhoto.currentIndex < lightboxPhoto.photos.length - 1 ? lightboxPhoto.currentIndex + 1 : 0;
    }
    
    setLightboxPhoto(prev => ({
      ...prev,
      currentIndex: newIndex,
      photo: lightboxPhoto.photos[newIndex]
    }));
  }, [lightboxPhoto.photos, lightboxPhoto.currentIndex]);

  // Gestion des touches clavier pour la lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (lightboxPhoto.isOpen) {
        switch (e.key) {
          case 'Escape':
            closeLightbox();
            break;
          case 'ArrowLeft':
            e.preventDefault();
            navigateLightbox('prev');
            break;
          case 'ArrowRight':
            e.preventDefault();
            navigateLightbox('next');
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [lightboxPhoto.isOpen, lightboxPhoto.currentIndex, lightboxPhoto.photos.length, navigateLightbox]);

  const handleConfirmDelete = () => {
    const { type, id } = deleteConfirmation;
    
    switch (type) {
      case 'personne':
        handleDeletePersonne(id);
        break;
      case 'lieu':
        handleDeleteLieu(id);
        break;
      case 'note':
        handleDeleteNote(id);
        break;
      case 'photo':
        handleDeletePhoto(id);
        break;
      case 'fichier':
        handleDeleteFichier(id);
        break;
    }
    
    // Fermer le modal de confirmation après la suppression
    setDeleteConfirmation({ isOpen: false, type: '', id: '', name: '' });
  };

  if (!enquete) {
    return (
      <div 
        className="fixed inset-0 w-screen h-screen bg-black/50 backdrop-blur-sm flex items-center justify-center z-[99999]"
        style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100vw', 
          height: '100vh',
          zIndex: 99999
        }}
        onClick={onClose}
      >
        <div 
          className="osint-card p-6 rounded-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-white">Chargement...</p>
        </div>
      </div>
    );
  }

  const getStatutColor = (statut: string) => {
    switch (statut) {
      case 'en_cours': return 'bg-blue-600 text-white';
      case 'terminee': return 'bg-green-600 text-white';
      case 'en_attente': return 'bg-yellow-600 text-white';
      case 'archivee': return 'bg-gray-600 text-white';
      default: return 'bg-gray-600 text-white';
    }
  };

  const getPrioriteColor = (priorite: string) => {
    switch (priorite) {
      case 'haute': return 'bg-red-600 text-white';
      case 'moyenne': return 'bg-yellow-600 text-white';
      case 'basse': return 'bg-green-600 text-white';
      default: return 'bg-gray-600 text-white';
    }
  };

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: Eye },
    { id: 'elements', label: 'Éléments d\'enquête', icon: Users },
    { id: 'media', label: 'Médias & Documents', icon: Database },
    { id: 'timeline-reports', label: 'Timeline & Rapports', icon: Archive }
  ];

  return (
    <div 
      className="fixed inset-0 w-screen h-screen bg-black/50 backdrop-blur-sm flex items-start justify-center pt-2 z-[99999]"
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        width: '100vw', 
        height: '100vh',
        zIndex: 99999
      }}
      onClick={onClose}
    >
      <div 
        className="w-full max-w-7xl h-[98vh] mx-4 osint-card rounded-lg flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header moderne */}
        <div className="flex items-center justify-between p-8 border-b border-osint-light-gray/20 bg-gradient-to-r from-osint-gray/50 to-transparent">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 osint-gradient rounded-2xl flex items-center justify-center shadow-xl glow-pulse">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white tracking-tight">{enquete.titre}</h1>
                <div className="flex items-center space-x-3 mt-3">
                  <span className={`px-3 py-1.5 rounded-xl text-sm font-semibold ${getStatutColor(enquete.statut)} shadow-lg`}>
                    {enquete.statut.replace('_', ' ')}
                  </span>
                  <span className={`px-3 py-1.5 rounded-xl text-sm font-semibold ${getPrioriteColor(enquete.priorite)} shadow-lg`}>
                    {enquete.priorite}
                  </span>
                  <div className="text-sm text-gray-300 flex items-center space-x-2">
                    <span>•</span>
                    <span className="font-medium">{typeof enquete.client === 'object' ? enquete.client?.nom || 'Client inconnu' : enquete.client}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {shareStatus.isShared && shareStatus.sharedByName && (
              <div className="flex items-center space-x-1 text-xs text-gray-400">
                <Shield className="w-3 h-3" />
                <span>Partagé par {shareStatus.sharedByName}</span>
                {shareStatus.sharedAt && (
                  <span>le {new Date(shareStatus.sharedAt).toLocaleDateString()}</span>
                )}
              </div>
            )}
            <button className="flex items-center space-x-2 px-3 py-2 bg-osint-light-gray hover:bg-gray-600 rounded-lg text-white text-sm transition-colors">
              <Download className="w-4 h-4" />
              <span>Exporter</span>
            </button>
            <button 
              onClick={handleSave}
              disabled={isSaving || !onSave}
              className="flex items-center space-x-2 px-3 py-2 bg-osint-orange hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white text-sm transition-colors"
            >
              <Save className="w-4 h-4" />
              <span>{isSaving ? 'Sauvegarde...' : 'Sauvegarder'}</span>
            </button>
            <button
              onClick={onClose}
              className="flex items-center space-x-2 px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm transition-colors"
            >
              <X className="w-4 h-4" />
              <span>Fermer</span>
            </button>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Sidebar moderne avec onglets */}
          <div className="w-72 sidebar-modern">
            <nav className="p-6">
              <div className="space-y-2">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-6">Sections de l'enquête</h3>
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-4 rounded-xl text-left transition-all duration-300 group ${
                        activeTab === tab.id 
                          ? 'bg-osint-orange/20 text-white border border-osint-orange/30 shadow-lg shadow-osint-orange/10' 
                          : 'text-gray-300 hover:bg-osint-orange/10 hover:text-osint-orange hover:border-osint-orange/20 border border-transparent'
                      }`}
                    >
                      <div className={`p-2 rounded-lg ${activeTab === tab.id ? 'bg-osint-orange' : 'bg-osint-orange/20 group-hover:bg-osint-orange/30'}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className="font-medium">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </nav>
          </div>

          {/* Contenu principal avec scrollbar personnalisé */}
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            {/* Vue d'ensemble - utilise EnhancedOverview pour combiner vue d'ensemble + infos client */}
            {activeTab === 'overview' && (
              <div className="slide-in">
                <EnhancedOverview
                  enquete={enquete}
                  onNavigate={(section, subsection) => {
                    // Navigation intelligente vers les bonnes sections
                    if (section === 'personnes' || section === 'lieux' || section === 'notes') {
                      setActiveTab('elements');
                    } else if (section === 'photos' || section === 'fichiers') {
                      setActiveTab('media');
                    } else if (section === 'timeline' || section === 'rapports') {
                      setActiveTab('timeline-reports');
                    }
                  }}
                  onAddPerson={() => setIsAddingPersonne(true)}
                  onAddLocation={() => setIsAddingLieu(true)}
                  onAddNote={() => setIsAddingNote(true)}
                  onAddPhoto={() => setIsAddingPhoto(true)}
                  onAddFile={() => setIsAddingFichier(true)}
                />
              </div>
            )}

            {/* Éléments d'enquête - utilise ElementsEnqueteTab pour combiner personnes + lieux + notes */}
            {activeTab === 'elements' && (
              <div className="slide-in">
                <ElementsEnqueteTab
                  enquete={enquete}
                  onAddPerson={() => setIsAddingPersonne(true)}
                  onAddLocation={() => setIsAddingLieu(true)}
                  onAddNote={() => setIsAddingNote(true)}
                />
              </div>
            )}

            {/* Médias & Documents - utilise MediaDocumentsTab pour combiner photos + fichiers */}
            {activeTab === 'media' && (
              <div className="slide-in">
                <MediaDocumentsTab
                  enquete={enquete}
                  onAddPhoto={() => setIsAddingPhoto(true)}
                  onAddFile={() => setIsAddingFichier(true)}
                />
              </div>
            )}

            {/* Timeline & Rapports - combine les deux sections existantes */}
            {activeTab === 'timeline-reports' && (
              <div className="p-6 space-y-8 slide-in">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Statistiques avec animation */}
                  <div className="osint-card p-6 rounded-2xl scale-in">
                    <h3 className="text-xl font-bold text-white mb-6 flex items-center space-x-2">
                      <div className="w-6 h-6 osint-gradient rounded-lg"></div>
                      <span>Statistiques</span>
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Personnes</span>
                        <span className="text-white font-medium">{enquete.personnes.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Lieux</span>
                        <span className="text-white font-medium">{enquete.lieux.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Notes</span>
                        <span className="text-white font-medium">{enquete.notes.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Photos</span>
                        <span className="text-white font-medium">{enquete.photos.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Fichiers</span>
                        <span className="text-white font-medium">{enquete.fichiers.length}</span>
                      </div>
                    </div>
                  </div>

                  {/* Informations générales */}
                  <div className="osint-card p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-white mb-4">Informations</h3>
                    <div className="space-y-3">
                      <div>
                        <span className="text-gray-400 text-sm">Client</span>
                        <p className="text-white">{typeof enquete.client === 'object' ? enquete.client?.nom || 'Client inconnu' : enquete.client}</p>
                      </div>
                      <div>
                        <span className="text-gray-400 text-sm">Budget</span>
                        <p className="text-white">{enquete.budget}€</p>
                      </div>
                      <div>
                        <span className="text-gray-400 text-sm">Assigné à</span>
                        <p className="text-white">{enquete.assigne}</p>
                      </div>
                      <div>
                        <span className="text-gray-400 text-sm">Créée le</span>
                        <p className="text-white">{enquete.dateCreation}</p>
                      </div>
                    </div>
                  </div>

                  {/* Services d'expert */}
                  <div className="osint-card p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-4">
                      <Shield className="w-5 h-5 text-purple-400" />
                      <h3 className="text-lg font-semibold text-white">Services d'expert</h3>
                    </div>
                    <div className="space-y-3">
                      <div className="text-sm text-gray-400 mb-3">
                        Besoin d'aide pour votre enquête ? Faites appel à nos experts certifiés.
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="bg-purple-900/30 p-2 rounded">
                          <div className="text-purple-300 font-medium">Email/Téléphone</div>
                          <div className="text-gray-400">50€ - 24-48h</div>
                        </div>
                        <div className="bg-purple-900/30 p-2 rounded">
                          <div className="text-purple-300 font-medium">Réseaux sociaux</div>
                          <div className="text-gray-400">100€ - 2-3 jours</div>
                        </div>
                        <div className="bg-purple-900/30 p-2 rounded">
                          <div className="text-purple-300 font-medium">Rapport OSINT</div>
                          <div className="text-gray-400">500€ - 5-7 jours</div>
                        </div>
                        <div className="bg-purple-900/30 p-2 rounded">
                          <div className="text-purple-300 font-medium">Patrimoine</div>
                          <div className="text-gray-400">1000€ - 7-10 jours</div>
                        </div>
                      </div>
                      <button
                        onClick={() => setIsExpertModalOpen(true)}
                        className="w-full mt-3 px-3 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 rounded-lg text-white text-sm font-medium flex items-center justify-center space-x-2 transition-all duration-200"
                      >
                        <Shield className="w-4 h-4" />
                        <span>Commander un service</span>
                      </button>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="osint-card p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-white mb-4">Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {enquete.tags.map(tag => (
                        <span key={tag} className="px-2 py-1 bg-osint-orange/20 text-osint-orange text-sm rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="osint-card p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-white mb-4">Description</h3>
                  <p className="text-gray-300">{enquete.description}</p>
                </div>

                {/* Timeline récente */}
                <div className="osint-card p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-white mb-4">Activité récente</h3>
                  <div className="space-y-3">
                    {enquete.timeline.slice(0, 5).map(item => (
                      <div key={item.id} className="flex items-center space-x-3 p-3 bg-osint-light-gray rounded-lg">
                        <div className="w-2 h-2 bg-osint-orange rounded-full"></div>
                        <div className="flex-1">
                          <p className="text-white text-sm font-medium">{item.titre}</p>
                          <p className="text-gray-400 text-xs">{item.date} • {item.auteur}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'client' && (
              <div className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-white">Informations Client</h2>
                  <button 
                    onClick={() => setIsEditingClient(true)}
                    className="flex items-center space-x-2 px-3 py-2 bg-osint-orange hover:bg-orange-600 rounded-lg text-white text-sm transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                    <span>Modifier</span>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="osint-card p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-white mb-4">Coordonnées</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Nom</label>
                        <p className="text-white">{enquete.infosClient.nom}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                        <p className="text-white">{enquete.infosClient.email}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Téléphone</label>
                        <p className="text-white">{enquete.infosClient.telephone}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Adresse</label>
                        <p className="text-white">{enquete.infosClient.adresse}</p>
                      </div>
                    </div>
                  </div>

                  <div className="osint-card p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-white mb-4">Notes client</h3>
                    <p className="text-gray-300">{enquete.infosClient.notes}</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'personnes' && (
              <div className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-white">Personnes d'intérêt</h2>
                  <button 
                    onClick={() => setIsAddingPersonne(true)}
                    className="flex items-center space-x-2 px-3 py-2 bg-osint-orange hover:bg-orange-600 rounded-lg text-white text-sm transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Ajouter une personne</span>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {enquete.personnes.map(personne => (
                    <div key={personne.id} className="osint-card p-6 rounded-lg">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-white">{personne.nom}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          personne.statut === 'actif' ? 'bg-green-600 text-white' :
                          personne.statut === 'suspect' ? 'bg-red-600 text-white' :
                          'bg-gray-600 text-white'
                        }`}>
                          {personne.statut}
                        </span>
                      </div>
                      
                      <div className="space-y-3">
                        <div>
                          <span className="text-gray-400 text-sm">Email</span>
                          <p className="text-white">{personne.email}</p>
                        </div>
                        <div>
                          <span className="text-gray-400 text-sm">Téléphone</span>
                          <p className="text-white">{personne.telephone}</p>
                        </div>
                        <div>
                          <span className="text-gray-400 text-sm">Adresse</span>
                          <p className="text-white">{personne.adresse}</p>
                        </div>
                        <div>
                          <span className="text-gray-400 text-sm">Description</span>
                          <p className="text-white text-sm">{personne.description}</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {personne.tags.map(tag => (
                            <span key={tag} className="px-2 py-1 bg-osint-orange/20 text-osint-orange text-xs rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div className="flex space-x-2 mt-4">
                        <button 
                          onClick={() => setEditingPersonne(personne)}
                          className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm transition-colors"
                          title="Modifier cette personne"
                        >
                          <Edit className="w-4 h-4" />
                          <span>Modifier</span>
                        </button>
                        <button 
                          onClick={() => confirmDelete('personne', personne.id, personne.nom)}
                          className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm transition-colors"
                          title="Supprimer cette personne"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>Supprimer</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'lieux' && (
              <div className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-white">Lieux</h2>
                  <button 
                    onClick={() => setIsAddingLieu(true)}
                    className="flex items-center space-x-2 px-3 py-2 bg-osint-orange hover:bg-orange-600 rounded-lg text-white text-sm transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Ajouter un lieu</span>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {enquete.lieux.map(lieu => (
                    <div key={lieu.id} className="osint-card p-6 rounded-lg">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-white">{lieu.nom}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          lieu.type === 'domicile' ? 'bg-blue-600 text-white' :
                          lieu.type === 'travail' ? 'bg-green-600 text-white' :
                          lieu.type === 'frequentation' ? 'bg-yellow-600 text-white' :
                          'bg-gray-600 text-white'
                        }`}>
                          {lieu.type}
                        </span>
                      </div>
                      
                      <div className="space-y-3">
                        <div>
                          <span className="text-gray-400 text-sm">Adresse</span>
                          <p className="text-white">{lieu.adresse}</p>
                        </div>
                        <div>
                          <span className="text-gray-400 text-sm">Coordonnées</span>
                          <p className="text-white">{lieu.coordonnees.lat}, {lieu.coordonnees.lng}</p>
                        </div>
                        <div>
                          <span className="text-gray-400 text-sm">Description</span>
                          <p className="text-white text-sm">{lieu.description}</p>
                        </div>
                        <div>
                          <span className="text-gray-400 text-sm">Photos</span>
                          <p className="text-white">{lieu.photos.length} photo(s)</p>
                        </div>
                      </div>
                      
                      <div className="flex space-x-2 mt-4">
                        <button 
                          onClick={() => setEditingLieu(lieu)}
                          className="flex-1 flex items-center justify-center space-x-1 px-2 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm transition-colors"
                          title="Modifier ce lieu"
                        >
                          <Edit className="w-4 h-4" />
                          <span className="hidden sm:inline">Modifier</span>
                        </button>
                        <button 
                          className="flex-1 flex items-center justify-center space-x-1 px-2 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white text-sm transition-colors"
                          title="Ajouter une photo de ce lieu"
                        >
                          <Camera className="w-4 h-4" />
                          <span className="hidden sm:inline">Photo</span>
                        </button>
                        <button 
                          onClick={() => confirmDelete('lieu', lieu.id, lieu.nom)}
                          className="flex-1 flex items-center justify-center space-x-1 px-2 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm transition-colors"
                          title="Supprimer ce lieu"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span className="hidden sm:inline">Supprimer</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'notes' && (
              <div className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-white">Notes</h2>
                  <button 
                    onClick={() => setIsAddingNote(true)}
                    className="flex items-center space-x-2 px-3 py-2 bg-osint-orange hover:bg-orange-600 rounded-lg text-white text-sm transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Nouvelle note</span>
                  </button>
                </div>

                <div className="space-y-4">
                  {enquete.notes.map(note => (
                    <div key={note.id} className="osint-card p-6 rounded-lg">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-white">{note.titre}</h3>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPrioriteColor(note.priorite)}`}>
                            {note.priorite}
                          </span>
                          <span className="text-gray-400 text-sm">{note.date}</span>
                        </div>
                      </div>
                      
                      <p className="text-gray-300 mb-4">{note.contenu}</p>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex flex-wrap gap-2">
                          {note.tags.map(tag => (
                            <span key={tag} className="px-2 py-1 bg-osint-orange/20 text-osint-orange text-xs rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => setEditingNote(note)}
                            className="flex items-center space-x-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm transition-colors"
                            title="Modifier cette note"
                          >
                            <Edit className="w-4 h-4" />
                            <span>Modifier</span>
                          </button>
                          <button 
                            onClick={() => confirmDelete('note', note.id, note.titre)}
                            className="flex items-center space-x-2 px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm transition-colors"
                            title="Supprimer cette note"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>Supprimer</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'photos' && (
              <div className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-white">Photos</h2>
                  <button 
                    onClick={() => setIsAddingPhoto(true)}
                    className="flex items-center space-x-2 px-3 py-2 bg-osint-orange hover:bg-orange-600 rounded-lg text-white text-sm transition-colors"
                  >
                    <Upload className="w-4 h-4" />
                    <span>Ajouter des photos</span>
                  </button>
                </div>

                {enquete.photos.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-24 h-24 bg-osint-light-gray rounded-full flex items-center justify-center mx-auto mb-4">
                      <Image className="w-12 h-12 text-gray-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">Aucune photo</h3>
                    <p className="text-gray-400 mb-6">Ajoutez des photos pour enrichir votre enquête</p>
                    <button 
                      onClick={() => setIsAddingPhoto(true)}
                      className="flex items-center space-x-2 px-4 py-2 bg-osint-orange hover:bg-orange-600 rounded-lg text-white text-sm transition-colors mx-auto"
                    >
                      <Upload className="w-4 h-4" />
                      <span>Ajouter votre première photo</span>
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {enquete.photos.map(photo => (
                    <div key={photo.id} className="osint-card p-4 rounded-lg hover:transform hover:scale-105 transition-all duration-200">
                      <div 
                        className="aspect-square bg-osint-light-gray rounded-lg mb-4 overflow-hidden relative group cursor-pointer"
                        onClick={() => photo.url && openLightbox(photo, enquete.photos)}
                      >
                        {photo.url ? (
                          <>
                            <img
                              src={photo.url}
                              alt={photo.nom}
                              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const fallback = target.nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                              <Eye className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            </div>
                            {/* Fallback pour les erreurs d'images */}
                            <div className="w-full h-full bg-osint-light-gray rounded-lg flex items-center justify-center" style={{display: 'none'}}>
                              <Image className="w-12 h-12 text-gray-400" />
                            </div>
                          </>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="w-12 h-12 text-gray-400" />
                          </div>
                        )}
                        {photo.tags.length > 0 && (
                          <div className="absolute top-2 left-2">
                            <span className="px-2 py-1 bg-osint-orange/90 text-white text-xs rounded">
                              {photo.tags.length} tag{photo.tags.length > 1 ? 's' : ''}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <h3 className="text-white font-medium mb-2 truncate" title={photo.nom}>
                        {photo.nom}
                      </h3>
                      <p className="text-gray-400 text-sm mb-2 line-clamp-2" title={photo.description}>
                        {photo.description || 'Aucune description'}
                      </p>
                      <div className="flex items-center text-gray-500 text-xs mb-3">
                        <span>{photo.date}</span>
                        {photo.lieu && (
                          <>
                            <span className="mx-1">•</span>
                            <span className="truncate" title={photo.lieu}>{photo.lieu}</span>
                          </>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-1 mb-3 max-h-8 overflow-hidden">
                        {photo.tags.slice(0, 2).map(tag => (
                          <span key={tag} className="px-2 py-1 bg-osint-orange/20 text-osint-orange text-xs rounded truncate">
                            {tag}
                          </span>
                        ))}
                        {photo.tags.length > 2 && (
                          <span className="px-2 py-1 bg-gray-600/20 text-gray-400 text-xs rounded">
                            +{photo.tags.length - 2}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex space-x-1">
                        <button 
                          onClick={() => setEditingPhoto(photo)}
                          className="flex-1 px-2 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm transition-colors flex items-center justify-center space-x-1"
                          title="Modifier cette photo"
                        >
                          <Edit className="w-4 h-4" />
                          <span className="hidden md:inline">Modifier</span>
                        </button>
                        <button 
                          onClick={() => photo.url && openLightbox(photo, enquete.photos)}
                          className="flex-1 px-2 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white text-sm transition-colors flex items-center justify-center space-x-1"
                          title="Voir en grand"
                        >
                          <Eye className="w-4 h-4" />
                          <span className="hidden md:inline">Voir</span>
                        </button>
                        <button 
                          onClick={() => {
                            if (photo.url) {
                              const link = document.createElement('a');
                              link.href = photo.url;
                              link.download = photo.nom || 'photo';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }
                          }}
                          className="flex-1 px-2 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-sm transition-colors flex items-center justify-center space-x-1"
                          title="Télécharger cette photo"
                        >
                          <Download className="w-4 h-4" />
                          <span className="hidden md:inline">Télécharger</span>
                        </button>
                        <button 
                          onClick={() => confirmDelete('photo', photo.id, photo.nom)}
                          className="flex-1 px-2 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm transition-colors flex items-center justify-center space-x-1"
                          title="Supprimer cette photo"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span className="hidden md:inline">Supprimer</span>
                        </button>
                      </div>
                    </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'fichiers' && (
              <div className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-white">Fichiers</h2>
                  <button 
                    onClick={() => setIsAddingFichier(true)}
                    className="flex items-center space-x-2 px-3 py-2 bg-osint-orange hover:bg-orange-600 rounded-lg text-white text-sm transition-colors"
                  >
                    <Upload className="w-4 h-4" />
                    <span>Ajouter des fichiers</span>
                  </button>
                </div>

                <div className="space-y-4">
                  {enquete.fichiers.map(fichier => (
                    <div key={fichier.id} className="osint-card p-6 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-osint-light-gray rounded-lg flex items-center justify-center">
                            <File className="w-6 h-6 text-gray-400" />
                          </div>
                          <div>
                            <h3 className="text-white font-medium">{fichier.nom}</h3>
                            <p className="text-gray-400 text-sm">{fichier.description}</p>
                            <p className="text-gray-500 text-xs">{fichier.date} • {fichier.taille}</p>
                          </div>
                        </div>
                        
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => setEditingFichier(fichier)}
                            className="flex items-center space-x-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm transition-colors"
                            title="Modifier ce fichier"
                          >
                            <Edit className="w-4 h-4" />
                            <span>Modifier</span>
                          </button>
                          <button 
                            onClick={() => {
                              if (fichier.url) {
                                const link = document.createElement('a');
                                link.href = fichier.url;
                                link.download = fichier.nom || 'fichier';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }
                            }}
                            className="flex items-center space-x-2 px-3 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white text-sm transition-colors"
                            title="Télécharger ce fichier"
                          >
                            <Download className="w-4 h-4" />
                            <span>Télécharger</span>
                          </button>
                          <button 
                            onClick={() => confirmDelete('fichier', fichier.id, fichier.nom)}
                            className="flex items-center space-x-2 px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm transition-colors"
                            title="Supprimer ce fichier"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>Supprimer</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'timeline' && (
              <div className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-white">Timeline</h2>
                  <div className="flex items-center space-x-2">
                    <select className="osint-input px-3 py-2 rounded-lg text-white">
                      <option>Tous les types</option>
                      <option>Notes</option>
                      <option>Photos</option>
                      <option>Lieux</option>
                      <option>Personnes</option>
                      <option>Fichiers</option>
                      <option>Statut</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-4">
                  {enquete.timeline.map(item => (
                    <div key={item.id} className="flex items-start space-x-4 p-4 bg-osint-light-gray rounded-lg">
                      <div className="w-3 h-3 bg-osint-orange rounded-full mt-2"></div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-white font-medium">{item.titre}</h3>
                          <span className="text-gray-400 text-sm">{item.date}</span>
                        </div>
                        <p className="text-gray-300 text-sm mb-2">{item.description}</p>
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-500 text-xs">{item.auteur}</span>
                          <span className="text-gray-500 text-xs">•</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            item.type === 'note' ? 'bg-blue-600 text-white' :
                            item.type === 'photo' ? 'bg-green-600 text-white' :
                            item.type === 'lieu' ? 'bg-yellow-600 text-white' :
                            item.type === 'personne' ? 'bg-purple-600 text-white' :
                            item.type === 'fichier' ? 'bg-gray-600 text-white' :
                            'bg-orange-600 text-white'
                          }`}>
                            {item.type}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'rapports' && (
              <div className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-white">Génération de Rapports</h2>
                  <button 
                    onClick={handleGenerateReport}
                    disabled={isGeneratingReport}
                    className="flex items-center space-x-2 px-4 py-2 bg-osint-orange hover:bg-orange-600 disabled:bg-gray-600 rounded-lg text-white text-sm transition-colors"
                  >
                    {isGeneratingReport ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Génération en cours...</span>
                      </>
                    ) : (
                      <>
                        <FileBarChart className="w-4 h-4" />
                        <span>Générer le rapport PDF</span>
                      </>
                    )}
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Options de rapport */}
                  <div className="osint-card p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-white mb-4">Options du rapport</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <label className="text-gray-300">Inclure les statistiques</label>
                        <input
                          type="checkbox"
                          checked={reportOptions.includeStats}
                          onChange={(e) => setReportOptions(prev => ({ ...prev, includeStats: e.target.checked }))}
                          className="w-4 h-4 text-osint-orange bg-osint-light-gray border-gray-600 rounded focus:ring-osint-orange focus:ring-2"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-gray-300">Inclure les informations client</label>
                        <input
                          type="checkbox"
                          checked={reportOptions.includeClient}
                          onChange={(e) => setReportOptions(prev => ({ ...prev, includeClient: e.target.checked }))}
                          className="w-4 h-4 text-osint-orange bg-osint-light-gray border-gray-600 rounded focus:ring-osint-orange focus:ring-2"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-gray-300">Inclure les personnes d'intérêt</label>
                        <input
                          type="checkbox"
                          checked={reportOptions.includePersonnes}
                          onChange={(e) => setReportOptions(prev => ({ ...prev, includePersonnes: e.target.checked }))}
                          className="w-4 h-4 text-osint-orange bg-osint-light-gray border-gray-600 rounded focus:ring-osint-orange focus:ring-2"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-gray-300">Inclure les lieux</label>
                        <input
                          type="checkbox"
                          checked={reportOptions.includeLieux}
                          onChange={(e) => setReportOptions(prev => ({ ...prev, includeLieux: e.target.checked }))}
                          className="w-4 h-4 text-osint-orange bg-osint-light-gray border-gray-600 rounded focus:ring-osint-orange focus:ring-2"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-gray-300">Inclure les notes</label>
                        <input
                          type="checkbox"
                          checked={reportOptions.includeNotes}
                          onChange={(e) => setReportOptions(prev => ({ ...prev, includeNotes: e.target.checked }))}
                          className="w-4 h-4 text-osint-orange bg-osint-light-gray border-gray-600 rounded focus:ring-osint-orange focus:ring-2"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-gray-300">Inclure les photos</label>
                        <input
                          type="checkbox"
                          checked={reportOptions.includePhotos}
                          onChange={(e) => setReportOptions(prev => ({ ...prev, includePhotos: e.target.checked }))}
                          className="w-4 h-4 text-osint-orange bg-osint-light-gray border-gray-600 rounded focus:ring-osint-orange focus:ring-2"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-gray-300">Inclure les fichiers</label>
                        <input
                          type="checkbox"
                          checked={reportOptions.includeFichiers}
                          onChange={(e) => setReportOptions(prev => ({ ...prev, includeFichiers: e.target.checked }))}
                          className="w-4 h-4 text-osint-orange bg-osint-light-gray border-gray-600 rounded focus:ring-osint-orange focus:ring-2"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-gray-300">Inclure la timeline</label>
                        <input
                          type="checkbox"
                          checked={reportOptions.includeTimeline}
                          onChange={(e) => setReportOptions(prev => ({ ...prev, includeTimeline: e.target.checked }))}
                          className="w-4 h-4 text-osint-orange bg-osint-light-gray border-gray-600 rounded focus:ring-osint-orange focus:ring-2"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Aperçu du rapport */}
                  <div className="osint-card p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-white mb-4">Aperçu du rapport</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Titre du rapport</span>
                        <span className="text-white font-medium">Rapport d'enquête - {enquete.titre}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Date de génération</span>
                        <span className="text-white font-medium">{new Date().toLocaleDateString('fr-FR')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Sections incluses</span>
                        <span className="text-white font-medium">
                          {Object.values(reportOptions).filter(Boolean).length} / {Object.keys(reportOptions).length}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Format</span>
                        <span className="text-white font-medium">PDF</span>
                      </div>
                    </div>

                    <div className="mt-6 p-4 bg-osint-light-gray rounded-lg">
                      <h4 className="text-white font-medium mb-2">Sections qui seront incluses :</h4>
                      <ul className="space-y-1 text-sm text-gray-300">
                        {reportOptions.includeStats && <li>• Statistiques de l'enquête</li>}
                        {reportOptions.includeClient && <li>• Informations client</li>}
                        {reportOptions.includePersonnes && <li>• Personnes d'intérêt ({enquete.personnes.length})</li>}
                        {reportOptions.includeLieux && <li>• Lieux ({enquete.lieux.length})</li>}
                        {reportOptions.includeNotes && <li>• Notes ({enquete.notes.length})</li>}
                        {reportOptions.includePhotos && <li>• Photos ({enquete.photos.length})</li>}
                        {reportOptions.includeFichiers && <li>• Fichiers ({enquete.fichiers.length})</li>}
                        {reportOptions.includeTimeline && <li>• Timeline ({enquete.timeline.length} événements)</li>}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Modèles de rapports prédéfinis */}
                <div className="osint-card p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-white mb-4">Modèles de rapports prédéfinis</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                      onClick={() => setReportOptions({
                        includeClient: true,
                        includePersonnes: true,
                        includeLieux: true,
                        includeNotes: true,
                        includePhotos: true,
                        includeFichiers: true,
                        includeTimeline: true,
                        includeStats: true,
                        includeObjectifs: true,
                        includeElementsFournis: true,
                        includeSommaire: true,
                        includeConclusions: true
                      })}
                      className="p-4 border border-osint-light-gray rounded-lg text-left hover:border-osint-orange transition-colors"
                    >
                      <h4 className="text-white font-medium mb-2">Rapport complet</h4>
                      <p className="text-gray-400 text-sm">Toutes les informations de l'enquête</p>
                    </button>
                    
                    <button
                      onClick={() => setReportOptions({
                        includeClient: true,
                        includePersonnes: true,
                        includeLieux: false,
                        includeNotes: true,
                        includePhotos: false,
                        includeFichiers: false,
                        includeTimeline: true,
                        includeStats: true,
                        includeObjectifs: true,
                        includeElementsFournis: false,
                        includeSommaire: true,
                        includeConclusions: true
                      })}
                      className="p-4 border border-osint-light-gray rounded-lg text-left hover:border-osint-orange transition-colors"
                    >
                      <h4 className="text-white font-medium mb-2">Rapport synthèse</h4>
                      <p className="text-gray-400 text-sm">Informations essentielles uniquement</p>
                    </button>
                    
                    <button
                      onClick={() => setReportOptions({
                        includeClient: true,
                        includePersonnes: true,
                        includeLieux: true,
                        includeNotes: false,
                        includePhotos: false,
                        includeFichiers: false,
                        includeTimeline: false,
                        includeStats: false,
                        includeObjectifs: true,
                        includeElementsFournis: false,
                        includeSommaire: true,
                        includeConclusions: true
                      })}
                      className="p-4 border border-osint-light-gray rounded-lg text-left hover:border-osint-orange transition-colors"
                    >
                      <h4 className="text-white font-medium mb-2">Rapport client</h4>
                      <p className="text-gray-400 text-sm">Informations pour le client</p>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modaux */}
      <PersonneModal
        isOpen={isAddingPersonne || editingPersonne !== null}
        onClose={() => {
          setIsAddingPersonne(false);
          setEditingPersonne(null);
        }}
        personne={editingPersonne}
        onSave={editingPersonne ? handleEditPersonne : handleAddPersonne}
        mode={editingPersonne ? 'edit' : 'add'}
      />

      <LieuModal
        isOpen={isAddingLieu || editingLieu !== null}
        onClose={() => {
          setIsAddingLieu(false);
          setEditingLieu(null);
        }}
        lieu={editingLieu}
        onSave={editingLieu ? handleEditLieu : handleAddLieu}
        mode={editingLieu ? 'edit' : 'add'}
      />

      <NoteModal
        isOpen={isAddingNote || editingNote !== null}
        onClose={() => {
          setIsAddingNote(false);
          setEditingNote(null);
        }}
        note={editingNote}
        onSave={editingNote ? handleEditNote : handleAddNote}
        mode={editingNote ? 'edit' : 'add'}
      />

      {/* Lightbox pour les photos */}
      {lightboxPhoto.isOpen && lightboxPhoto.photo && (
        <div 
          className="fixed inset-0 w-screen h-screen bg-black/90 backdrop-blur-md z-[99999] flex items-center justify-center"
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            width: '100vw', 
            height: '100vh',
            zIndex: 99999
          }}
          onClick={closeLightbox}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full h-full flex items-center justify-center">
            {/* Bouton fermer */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Navigation précédente */}
            {lightboxPhoto.photos.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateLightbox('prev');
                }}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
              >
                <span className="text-2xl">‹</span>
              </button>
            )}
            
            {/* Navigation suivante */}
            {lightboxPhoto.photos.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateLightbox('next');
                }}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
              >
                <span className="text-2xl">›</span>
              </button>
            )}
            
            {/* Image principale */}
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <img
                src={lightboxPhoto.photo.url}
                alt={lightboxPhoto.photo.nom}
                className="max-w-full max-h-[80vh] object-contain"
              />
              
              {/* Informations de l'image */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <h3 className="text-white text-xl font-bold mb-2">{lightboxPhoto.photo.nom}</h3>
                {lightboxPhoto.photo.description && (
                  <p className="text-gray-300 mb-2">{lightboxPhoto.photo.description}</p>
                )}
                <div className="flex items-center text-gray-400 text-sm mb-3">
                  <span>{lightboxPhoto.photo.date}</span>
                  {lightboxPhoto.photo.lieu && (
                    <>
                      <span className="mx-2">•</span>
                      <span>{lightboxPhoto.photo.lieu}</span>
                    </>
                  )}
                  {lightboxPhoto.photos.length > 1 && (
                    <>
                      <span className="mx-2">•</span>
                      <span>{lightboxPhoto.currentIndex + 1} / {lightboxPhoto.photos.length}</span>
                    </>
                  )}
                </div>
                {lightboxPhoto.photo.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {lightboxPhoto.photo.tags.map((tag: string) => (
                      <span key={tag} className="px-3 py-1 bg-osint-orange/80 text-white text-sm rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <PhotoModal
        isOpen={isAddingPhoto || editingPhoto !== null}
        onClose={() => {
          setIsAddingPhoto(false);
          setEditingPhoto(null);
        }}
        photo={editingPhoto}
        onSave={editingPhoto ? handleEditPhoto : handleAddPhoto}
        mode={editingPhoto ? 'edit' : 'add'}
        enqueteId={enqueteId}
      />

      <FichierModal
        isOpen={isAddingFichier || editingFichier !== null}
        onClose={() => {
          setIsAddingFichier(false);
          setEditingFichier(null);
        }}
        fichier={editingFichier}
        onSave={editingFichier ? handleEditFichier : handleAddFichier}
        mode={editingFichier ? 'edit' : 'add'}
      />

      <ClientModal
        isOpen={isEditingClient}
        onClose={() => setIsEditingClient(false)}
        infosClient={enquete?.infosClient || { nom: '', email: '', telephone: '', adresse: '', notes: '' }}
        onSave={handleUpdateClient}
      />

      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        onClose={() => setDeleteConfirmation({ isOpen: false, type: '', id: '', name: '' })}
        onConfirm={handleConfirmDelete}
        title="Confirmer la suppression"
        message={`Êtes-vous sûr de vouloir supprimer "${deleteConfirmation.name}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        type="danger"
      />

      {isExpertModalOpen && (
        <ExpertModal 
          onClose={() => setIsExpertModalOpen(false)} 
          currentEnquete={enquete ? {
            id: enquete.id,
            titre: enquete.titre,
            description: enquete.description,
            client: enquete.client,
            is_shared_with_admin: shareStatus.isShared,
            shared_by_name: shareStatus.sharedByName,
            shared_at: shareStatus.sharedAt
          } : undefined}
          onShareStatusChange={handleShareStatusChange}
        />
      )}
    </div>
  );
};

export default EnqueteDetail; 